import { IShiftItem } from "../commands";
import { toFiscalDateTime } from "../fiscal-date";
import { IShiftsWindow } from "./shifts.models";

/**
 * Splits an inclusive date range into windows the server will accept: one per
 * **calendar month**, clamped to the range. Whole days, so a user picking
 * "12 August" gets all of 12 August.
 *
 * The server refuses a window wider than a month —
 * «Запит дозволяється в межах одного місяця» — counted in **calendar months**,
 * so 31 days from 1 January passes while 31 days from 1 February does not.
 * Splitting on month boundaries is therefore always within the limit.
 *
 * Bounds carry the local offset; see `toFiscalDateTime` for why UTC breaks
 * both this check and the day boundaries. Both rules are in
 * ai/docs/prro-api.md.
 */
export const toShiftWindows = ({
  from,
  to,
}: {
  from: string;
  to: string;
}): IShiftsWindow[] => {
  const start = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T23:59:59.999`);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return [];
  }

  const windows: IShiftsWindow[] = [];
  let windowStart = start;

  while (windowStart <= end) {
    // Last millisecond of `windowStart`'s month, or the end of the range.
    const monthEnd = new Date(
      windowStart.getFullYear(),
      windowStart.getMonth() + 1,
      1,
    );
    const windowEnd = new Date(Math.min(monthEnd.getTime() - 1, end.getTime()));

    windows.push({
      from: toFiscalDateTime(windowStart),
      to: toFiscalDateTime(windowEnd),
    });

    windowStart = new Date(windowEnd.getTime() + 1);
  }

  return windows;
};

/** Windows can overlap at their edges; a shift must be counted once. */
export const mergeShifts = (perWindow: IShiftItem[][]): IShiftItem[] => {
  const byId = new Map<number, IShiftItem>();

  perWindow.flat().forEach(shift => byId.set(shift.ShiftId, shift));

  return [...byId.values()].sort((a, b) => a.Opened.localeCompare(b.Opened));
};
